import {
  EOgqlJobState,
  IOgqlJobResult,
  IOgqlPollParams,
  IOgqlQueryResult,
  IQueryRequestParams
} from '@opengov/ogql-types';

import { getJob, postQuery } from './delphius-stubs';
import * as queryService from './query-service-stubs';
import { getStaticDelay } from './timing';

declare global {
  // tslint:disable-next-line:interface-name
  interface Window {
    activeOgqlQueries: number;
  }
}

if (!(process.versions && process.versions.node)) {
  window.activeOgqlQueries = 0;
}

function incrementQueryCount() {
  if (!(process.versions && process.versions.node)) {
    window.activeOgqlQueries++;
  }
}

function decrementQueryCount() {
  if (!(process.versions && process.versions.node)) {
    window.activeOgqlQueries--;
  }
}

// hostUrl, if present, will be env.queryServiceHost if we're using the queryService and env.ogqlHost if not
export async function queueQuery(
  params: IQueryRequestParams,
  clientOptions?: RequestInit,
  hostUrl?: string
): Promise<IOgqlJobResult | IOgqlQueryResult> {
  const { useQueryService } = params;
  incrementQueryCount();
  const runQuery = !!useQueryService ? queryService.runQuery : postQuery;
  return runQuery(params, clientOptions, hostUrl).catch(async rejection => {
    decrementQueryCount();
    return Promise.reject(rejection);
  });
}

export async function getJobResult(
  e: IOgqlPollParams,
  clientOptions?: RequestInit,
  hostUrl?: string
): Promise<IOgqlJobResult> {
  return getJob(e, clientOptions, hostUrl)
    .then(response => {
      if (response.state === EOgqlJobState.finished || response.state === EOgqlJobState.failed) {
        decrementQueryCount();
      }
      return response;
    })
    .catch(async rejection => {
      decrementQueryCount();
      return Promise.reject(rejection);
    });
}

// Given that we are polling 1x/s, this should cover the 99th percentile of query durations
const MAX_OGQL_V0_POLL_ATTEMPTS = 110;

const pollJob = async (e: IOgqlPollParams, clientOptions?: RequestInit, hostUrl?: string): Promise<IOgqlJobResult> => {
  if (e.pollAttempts >= MAX_OGQL_V0_POLL_ATTEMPTS) {
    return { id: e.id, state: EOgqlJobState.cancelled };
  }
  return getJobResult(e, clientOptions, hostUrl).then(async result => {
    switch (result.state) {
      case EOgqlJobState.finished:
      case EOgqlJobState.failed:
        return result;

      default:
        const pollDelay = getStaticDelay();
        return new Promise((resolve: Function) => setTimeout(resolve, pollDelay)).then(async () => {
          return pollJob(
            {
              ...e,
              pollAttempts: e.pollAttempts + 1,
              pollDelay: pollDelay
            },
            clientOptions,
            hostUrl
          );
        });
    }
  });
};

export function isQueryResult(result: IOgqlJobResult | IOgqlQueryResult): result is IOgqlQueryResult {
  const jobResult = result as IOgqlJobResult;
  return !jobResult.jobState && !jobResult.state;
}

// This function wraps queueQuery for the embed service,
// ensuring that it doesn't return the enqueued job before the job is complete
// hostUrl, if present, will be env.queryServiceHost if we're using the queryService and env.ogqlHost if not
export const query = async (e: IQueryRequestParams, clientOptions?: RequestInit, hostUrl?: string) =>
  queueQuery(e, clientOptions, hostUrl).then(async result => {
    if (isQueryResult(result)) {
      return { id: e.id, state: EOgqlJobState.finished, jobResult: result };
    }

    const queryState = result.jobState || result.state;
    switch (queryState) {
      case EOgqlJobState.enqueued:
      case EOgqlJobState.running:
        return pollJob(
          {
            ...e,
            id: result.id,
            pollAttempts: 0,
            pollDelay: getStaticDelay()
          },
          clientOptions,
          hostUrl
        );
      default:
        return result;
    }
  });

export { getView, getViewColumns, getViews } from './delphius-stubs';
export { getStaticDelay } from './timing';
